import { useState } from "react";
import { Spinner } from "@radix-ui/themes";
import formatPrice from "@/core/helpers/formatPrice";
import { BankCardIcon } from "@/shared/icons";
import Button from "@/shared/ui/button/Button";
import { getFeeData } from "@/domains/bank/helpers";
import toast from "@/domains/toasts/helpers/toast";
import { useBankCharacterQuery } from "@/domains/bank/hooks/useBankCharacterQuery";
import { useBankTransferMutation } from "@/domains/bank/hooks/useBankTransferMutation";
import { ATMInput } from "@/domains/bank/components/BankATM/components/ATMInput";
import { BankATMBackButton } from "@/domains/bank/components/BankATM/components/BankATMBackButton";

export function BankATMTransferScreenContainer({
  onCancel,
  atmId,
  maxCash,
  maxCard,
  feePercent,
}: {
  onCancel: () => void;
  atmId: number;
  maxCash: number;
  maxCard: number;
  feePercent: number;
}) {
  const [accountNumber, setAccountNumber] = useState(undefined);
  const [transferAmount, setTransferAmount] = useState(undefined);
  const transferMutation = useBankTransferMutation();
  const bankCharacterQuery = useBankCharacterQuery({ enabled: true });

  const ownAccountNumber = bankCharacterQuery.data?.accountNumber;
  const transferLimit = bankCharacterQuery.data?.tariff?.transferLimit || 0;
  const { feeAmount, transferWithFeeAmount, maxAmountBeforeFee } = getFeeData({
    balance: maxCard,
    feePercent,
    transferAmount,
    transferLimit: maxCard,
    minFeeAmount: 100,
  });

  function handleAmountChange(value: string) {
    const int = Number.parseInt(value, 10);
    setTransferAmount(int);
  }

  function handleMaxClick() {
    setTransferAmount(Math.min(maxAmountBeforeFee, transferLimit));
  }

  function handleTransfer() {
    console.log("Depositing:", transferAmount);

    if (accountNumber === ownAccountNumber) {
      toast.error("Невозможно перевести деньги на свой счет!");
      return;
    }

    transferMutation.mutate(
      {
        transferAmount: transferWithFeeAmount,
        accountNumber,
        atmId,
        filialId: null,
      },
      {
        onSuccess: () => {
          toast.success("Перевод средств выполнен успешно");
        },
        onError: () => {
          toast.error("Перевод средств не выполнен");
        },
      },
    );
  }

  return (
    <div className="relative h-full flex flex-col">
      <BankATMBackButton onClick={onCancel} />

      <div className="flex-1 flex flex-col justify-between w-full max-w-[440px] mx-auto">
        <div>
          <h2 className="text-18 font-bold text-dark-800 mb-6 text-center">Перевод средств</h2>

          <ATMInput
            value={accountNumber}
            onChange={(value) => setAccountNumber(value)}
            placeholder="Номер счета"
            className="mb-2"
            icon={<BankCardIcon className="size-6 text-[#186d27]" />}
          />

          <div className="mb-2">
            <ATMInput
              type="number"
              value={transferAmount}
              onChange={(value) => handleAmountChange(value)}
              onMaxClick={() => handleMaxClick()}
              maxValue={maxCash}
              className="mb-4"
            />

            <div className="flex items-center justify-between text-sbank-muted">
              <div className="text-normal text-15 text-dark-800">Комиссия:</div>{" "}
              <div className="text-14 text-[#186d27]">{formatPrice(feeAmount)} ₽</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-end leading-tight text-right gap-5">
          <div>
            <div className="text-18 font-bold text-[#186d27]">
              {formatPrice(transferWithFeeAmount)} ₽
            </div>
            <div className="text-sbank-muted text-normal text-15">
              Будет списано с учетом комиссии
            </div>
          </div>

          <Button
            onClick={() => handleTransfer()}
            disabled={!transferAmount || transferAmount <= 0 || transferAmount > maxCash}
            className="w-full h-[44px] shadow-[0px_6px_20px_0px_#186D2780]"
            data-mz-accent="green"
          >
            {transferMutation.isPending ? <Spinner className="size-5 animate-spin" /> : "Выполнить"}
          </Button>
        </div>
      </div>
    </div>
  );
}
